import { Component } from 'react'
import type { ErrorInfo, ReactNode } from 'react'

interface ErrorBoundaryProps {
  children: ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
}

const texts = {
  hu: {
    title: 'Hiba történt',
    description: 'Sajnáljuk, valami hiba történt. Kérjük, töltsd újra az oldalt.',
    reload: 'Oldal újratöltése',
  },
  ro: {
    title: 'A apărut o eroare',
    description: 'Ne pare rău, ceva nu a funcționat. Te rugăm să reîncarci pagina.',
    reload: 'Reîncarcă pagina',
  },
}

// Detect language from the URL without depending on the router/locale context,
// since this boundary lives outside the providers. Default to RO.
function getTexts() {
  if (typeof window !== 'undefined' && window.location.pathname.startsWith('/hu')) {
    return texts.hu
  }
  return texts.ro
}

export default class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log so the failure is visible in the console / monitoring instead of a white screen.
    console.error('Uncaught error:', error, errorInfo)
  }

  handleReload = () => {
    window.location.reload()
  }

  render() {
    if (this.state.hasError) {
      const t = getTexts()
      return (
        <div className="min-h-screen flex items-center justify-center p-6 bg-surface">
          <div className="max-w-md w-full text-center">
            <h1 className="font-heading font-semibold text-2xl text-secondary mb-3">
              {t.title}
            </h1>
            <p className="text-text-secondary mb-6">{t.description}</p>
            <button
              type="button"
              onClick={this.handleReload}
              className="inline-flex items-center justify-center font-medium rounded-btn transition-all duration-200 bg-primary text-white hover:bg-primary-hover px-8 py-3.5 text-base"
            >
              {t.reload}
            </button>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
